import React, { useEffect, useState, useMemo } from 'react';
import { useRecordContext, useDataProvider } from 'react-admin';
import { Box, Chip, Paper, Stack, Typography, Skeleton } from '@mui/material';
import moment from 'moment';
import { GiftRuleType } from '../../../../models';
import { UniqueCodeRuleCard } from './UniqueCodeRuleCard';

export const RuleList = (props) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [stationsMap, setStationsMap] = useState<Map<string, string>>(new Map());
    const [stationsLoading, setStationsLoading] = useState(false);
    const [fuelsMap, setFuelsMap] = useState<Map<string, string>>(new Map());
    const [fuelsLoading, setFuelsLoading] = useState(false);

    useEffect(() => {
        if (!record || !record.giftRules) return;

        // Fetch station names
        const stationIds = new Set<string>();
        record.giftRules.forEach((rule: any) => {
            rule.stationIds?.forEach((id: string) => stationIds.add(id));
        });

        if (stationIds.size > 0) {
            setStationsLoading(true);
            dataProvider.getMany('stations', {
                ids: Array.from(stationIds),
            }).then(({ data }) => {
                const map = new Map<string, string>();
                data.forEach((station: any) => {
                    map.set(station.id, station.name);
                });
                setStationsMap(map);
            }).catch(error => {
                console.error('Error fetching stations:', error);
            }).finally(() => setStationsLoading(false));
        } else {
            setStationsLoading(false);
        }

        // Fetch fuel names
        const fuelIds = new Set<string>();
        record.giftRules.forEach((rule: any) => {
            (rule.giftRuleFuels || []).forEach((grf: any) => fuelIds.add(grf.fuelId));
        });

        if (fuelIds.size > 0) {
            setFuelsLoading(true);
            dataProvider.getMany('fuels', {
                ids: Array.from(fuelIds),
            }).then(({ data }) => {
                const map = new Map<string, string>();
                data.forEach((fuel: any) => {
                    map.set(fuel.id, fuel.name);
                });
                setFuelsMap(map);
            }).catch(error => {
                console.error('Error fetching fuels:', error);
            }).finally(() => setFuelsLoading(false));
        } else {
            setFuelsLoading(false);
        }
    }, [record, dataProvider]);

    // Agrupar regras por groupId
    const groupedRules = useMemo(() => {
        if (!record || !record.giftRules) return [];

        const groups = new Map<number, any[]>();
        record.giftRules.forEach((rule: any) => {
            const groupId = rule.groupId ?? 0;
            if (!groups.has(groupId)) {
                groups.set(groupId, []);
            }
            groups.get(groupId)!.push(rule);
        });
        return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
    }, [record]);

    if (!record) { return null; }

    const hasMultipleGroups = groupedRules.length > 1;

    return (
        // padding can be overridden via props.padding (default 0)
        <Box display="flex" flexDirection="column" gap={2} sx={{ p: (props && props.padding) || 0 }}>
            {/* Explicação sobre as regras */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    Como funcionam as regras:
                </Typography>
                <Typography variant="body2" component="div">
                    {hasMultipleGroups ? (
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Dentro de cada <strong>GRUPO</strong>, basta atender <strong>QUALQUER UMA</strong> das regras</li>
                            <li>Para ganhar o brinde, é necessário atender <strong>TODOS OS GRUPOS</strong> (um após o outro)</li>
                            <li>Cada grupo funciona de forma <strong>EXCLUSIVA</strong>: cada abastecimento conta para apenas UM grupo</li>
                        </ul>
                    ) : (
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Basta atender <strong>QUALQUER UMA</strong> das regras abaixo para ganhar o brinde</li>
                        </ul>
                    )}
                </Typography>
            </Paper>

            {groupedRules.map(([groupId, rules], groupIndex) => (
                <React.Fragment key={groupId}>
                    <Paper
                        elevation={1}
                        sx={{
                            padding: 2,
                            border: '2px solid #e3f2fd',
                            borderRadius: 2,
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={600} color="primary" gutterBottom sx={{ mb: 2 }}>
                            Grupo {groupId + 1} - Basta atender QUALQUER UMA destas regras:
                        </Typography>

                        <Stack spacing={1.5}>
                            {rules.map((rule: any, ruleIndex: number) => (
                                <Paper
                                    key={ruleIndex}
                                    elevation={0}
                                    sx={{
                                        padding: rule.type === GiftRuleType.unique_code ? 0 : 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        backgroundColor: '#fff',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {rule.type === GiftRuleType.unique_code ? (
                                        <UniqueCodeRuleCard
                                            code={rule.code}
                                            chainCode={props?.chain?.code}
                                            giftId={record.id as string}
                                            useManualCode={rule.useManualCode}
                                            useQrCode={rule.useQrCode}
                                            requireLocation={rule.requireLocation}
                                            locationCoordinates={rule.locationCoordinates}
                                        />
                                    ) : (
                                    <Stack spacing={1.5}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {GiftRuleType.giftRuleTypeChoices.find(c => c.id === rule.type)?.name || rule.type}
                                            </Typography>
                                            {rule.giftRuleFuels?.length > 0 && rule.giftRuleFuels.map((grf: any) => (
                                                <Chip
                                                    key={grf.fuelId}
                                                    label={fuelsMap.get(grf.fuelId) || grf.fuelId}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>

                                        {rule.type === GiftRuleType.clube_certo ? (
                                            <>
                                                {/* Mostrar apenas Período de Verificação para Clube Certo */}
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Período de Verificação:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {rule.clubeCertoCheckPeriodDays || 30} dias
                                                    </Typography>
                                                </Box>
                                            </>
                                        ) : rule.type === GiftRuleType.prediction_winner ? (
                                            <>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                        Bolão vinculado:
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600} color="#7869DF">
                                                        ⚽ {rule.bolaoName || rule.bolaoId || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                                        Entregue manualmente pelo painel do bolão a quem acertar o placar.
                                                    </Typography>
                                                </Box>
                                            </>
                                        ) : rule.type === GiftRuleType.roleta_prize ? (
                                            <>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                        Roleta vinculada:
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600} color="#7869DF">
                                                        🎡 {rule.roletaName || rule.roletaId || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                                        Este brinde guarda os prêmios das fatias. A entrega acontece na hora em que o cliente gira.
                                                    </Typography>
                                                </Box>
                                            </>
                                        ) : GiftRuleType.isSaleType(rule.type) ? (
                                            <>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {rule.type === GiftRuleType.accumulated_sale ? 'Valor acumulado:' : 'Valor mínimo da compra:'}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {rule.value ? `R$ ${rule.value.toFixed(2)}` : 'Qualquer valor'}
                                                    </Typography>
                                                </Box>

                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Produtos:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {rule.giftRuleSaleProducts?.length > 0
                                                            ? `${rule.giftRuleSaleProducts.length} produto(s) de venda`
                                                            : rule.giftRuleProducts?.length > 0
                                                                ? `${rule.giftRuleProducts.length} produto(s) do catálogo`
                                                                : 'Todos os produtos'}
                                                    </Typography>
                                                </Box>

                                                {rule.type !== GiftRuleType.accumulated_sale && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Quantidade de compras:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {(rule.quantity ?? 1)} {(rule.quantity ?? 1) === 1 ? 'compra' : 'compras'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {/* Troca por Pontos não tem combustível: o cliente
                                                    paga em pontos, não abastece. */}
                                                {rule.type !== GiftRuleType.points_exchange && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Tipo de Combustível:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {rule.giftRuleFuels?.length > 0 ? (
                                                                fuelsLoading ? (
                                                                    <Skeleton variant="text" width={140} animation="wave" />
                                                                ) : (
                                                                    rule.giftRuleFuels.map((grf: any) => fuelsMap.get(grf.fuelId) || grf.fuelId).join(', ')
                                                                )
                                                            ) : (
                                                                'Todos os combustíveis'
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {rule.valueType && rule.value !== null && rule.value !== undefined && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {rule.type === GiftRuleType.points_exchange
                                                                ? 'Preço da troca'
                                                                : rule.valueType === 'volume' ? 'Volume' : 'Valor'}:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {rule.type === GiftRuleType.points_exchange
                                                                ? `${rule.value.toLocaleString('pt-BR')} pontos`
                                                                : rule.valueType === 'volume'
                                                                    ? `${rule.value.toFixed(2)} L`
                                                                    : `R$ ${rule.value.toFixed(2)}`
                                                            }
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {rule.type !== GiftRuleType.accumulated_fillin
                                                    && rule.type !== GiftRuleType.points_exchange && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Quantidade de abastecimentos:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {(rule.quantity ?? 1)} {(rule.quantity ?? 1) === 1 ? 'abastecimento' : 'abastecimentos'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}

                                        {/* Período e locais valem para todas as regras dirigidas
                                            por evento — abastecimento e compra usam o mesmo
                                            gift_rule_place. */}
                                        {GiftRuleType.isEventType(rule.type) && (
                                            <>
                                                {rule.startDate && rule.endDate && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Período:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {moment(rule.startDate).format('DD/MM/YYYY')} - {moment(rule.endDate).format('DD/MM/YYYY')}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {rule.stationIds && rule.stationIds.length > 0 && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                            {GiftRuleType.isSaleType(rule.type) ? 'Locais:' : 'Postos:'}
                                                        </Typography>
                                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                            {rule.stationIds.map((stationId: string) => (
                                                                stationsLoading ? (
                                                                    <Skeleton key={`s-${stationId}`} variant="rectangular" width={84} height={28} animation="wave" sx={{ borderRadius: 1 }} />
                                                                ) : (
                                                                    <Chip
                                                                        key={stationId}
                                                                        label={stationsMap.get(stationId) || stationId}
                                                                        size="small"
                                                                    />
                                                                )
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Stack>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    </Paper>

                    {groupIndex < groupedRules.length - 1 && (
                        <Box display="flex" justifyContent="center" my={1}>
                            <Chip
                                label="E TAMBÉM"
                                color="primary"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                }}
                            />
                        </Box>
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
}
